<template>
  <div class="tags">
    <b-overlay :show="checkingPermission">
      <div
        v-if="checkingPermission || canManagePermissions"
        id="tour-usertags"
      >
        <b-row class="custom-search justify-content-between mb-1">
          <b-col>
            <b-button
              variant="outline-primary"
              @click="openAdd"
            >
              <feather-icon icon="PlusIcon" />
              {{ $t("Add Tag") }}
            </b-button>
          </b-col>
        </b-row>

        <!-- Search for Mobile -->
        <b-form-group class="d-md-none">
          <b-form-input
            v-model="searchTerm"
            :placeholder="$t('Search')"
            type="text"
          />
        </b-form-group>

        <b-overlay :show="!checkingPermission && isProcessing">
          <vue-good-table
            :columns="columns"
            :rows="filteredRows"
          >
            <div slot="emptystate">
              <p
                v-if="!isProcessing"
                class="text-center mb-0"
              >
                {{ $t("No Data Found") }}
              </p>
              <p
                v-else
                class="text-center mb-0"
              >
                {{ $t("Loading...") }}
              </p>
            </div>

            <template
              slot="table-row"
              slot-scope="props"
            >
              <span v-if="props.column.field === 'preview'">
                <tag-badge
                  :name="props.row.name"
                  :color="props.row.color"
                  :description="props.row.description || ''"
                  size="small"
                />
              </span>

              <span v-else-if="props.column.field === 'color'">
                <div class="d-flex align-items-center">
                  <span
                    :style="{
                      width: '18px',
                      height: '18px',
                      backgroundColor: props.row.color,
                      display: 'inline-block',
                      borderRadius: '3px',
                      marginRight: '8px',
                      border: '1px solid rgba(0,0,0,0.1)',
                    }"
                  />
                  <span class="text-muted">{{ props.row.color }}</span>
                </div>
              </span>
              <span v-else-if="props.column.field === 'description'">
                {{ props.row.description }}
              </span>
              <span v-else-if="props.column.field === 'users'">
                <b-avatar-group>
                  <b-avatar
                    v-for="(user, index) in props.row.users?.data"
                    :key="user.uuid"
                    v-b-tooltip.hover="user.name"
                    :alt="user.name"
                    :text="getInitials(user.name)"
                    :variant="getRandomVariant()"
                    :src="getAvatar(user.avatar)"
                    :badge-variant="user.isOnline ? 'success' : 'secondary'"
                    :style="{ right: index !== 0 ? `${10 * index}px` : '' }"
                    badge
                    badge-left
                  />
                  <b-avatar
                    v-if="props.row.users?.total > 3"
                    size="32"
                    variant="secondary"
                    :style="{ marginLeft: '-32px' }"
                  >
                    <span class="small">+{{ props.row.users.total - 3 }}</span>
                  </b-avatar>
                </b-avatar-group>
              </span>
              <span v-else-if="props.column.field === 'actions'">
                <b-dropdown
                  right
                  size="sm"
                  variant="link"
                  toggle-class="text-muted"
                  no-caret
                >
                  <template #button-content>
                    <i class="fas fa-ellipsis-v" />
                  </template>
                  <b-dropdown-item
                    v-b-modal.assign-users-tags
                    @click="selectedTag = props.row"
                  >
                    <i class="fa-solid fa-user-plus" />
                    <span class="ml-1">{{ $t("Assign Users") }}</span>
                  </b-dropdown-item>

                  <b-dropdown-item @click="openEdit(props.row)">
                    <i class="fas fa-edit" />
                    <span class="ml-1">{{ $t("Edit") }}</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="confirmDelete(props.row)">
                    <i class="fas fa-trash-alt text-danger" />
                    <span class="ml-1">{{ $t("Delete") }}</span>
                  </b-dropdown-item>
                </b-dropdown>
              </span>
            </template>
          </vue-good-table>
        </b-overlay>

        <pagination
          v-if="rows.length"
          v-modal="currentPage"
          :total-items="totalItems"
          @onPaginationChange="loadTags"
        />
        <b-modal
          id="confirm-delete"
          ref="deleteModal"
          :title="$t('Confirm Delete')"
          hide-footer
        >
          <div class="my-3">
            {{ $t("Are you sure you want to delete this tag?") }}
          </div>
          <div class="d-flex justify-content-end">
            <b-button
              variant="secondary"
              @click="$refs.deleteModal.hide()"
            >{{
              $t("Cancel")
            }}</b-button>
            <b-button
              class="ml-2"
              variant="danger"
              @click="performDelete"
            >{{
              $t("Delete")
            }}</b-button>
          </div>
        </b-modal>
        <add-edit-tag
          ref="addEdit"
          @saved="loadTags"
          @deleted="loadTags"
        />
      </div>
      <no-permission v-else />
    </b-overlay>

    <assign-users
      v-if="selectedTag"
      :tag="selectedTag"
      @refetch-tags="loadTags"
    />
  </div>
</template>
<script>
import {
  BAvatarGroup,
  BAvatar,
  BFormGroup,
  BFormInput,
  BButton,
  BOverlay,
  BRow,
  BCol,
  BDropdown,
  BDropdownItem,
  BModal,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import Pagination from '@/views/common/components/Pagination.vue'
import NoPermission from '@/views/common/components/NoPermission.vue'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import useApollo from '@/plugins/graphql/useApollo'
import TagBadge from './Components/TagBadge.vue'
import AddEditTag from './Components/AddEditTag.vue'
import AssignUsers from './Components/AssignUsers.vue'

export default {
  components: {
    VueGoodTable,
    BAvatarGroup,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    Pagination,
    BButton,
    BDropdown,
    BDropdownItem,
    BModal,
    BOverlay,
    NoPermission,
    TagBadge,
    AddEditTag,
    AssignUsers,
  },

  data() {
    return {
      pageLength: 10,
      dir: false,
      rows: [],
      currentPage: 1,
      totalItems: 0,
      columns: [
        { label: this.$t('Tag'), field: 'name' },
        { label: this.$t('Description'), field: 'description' },
        { label: this.$t('Color'), field: 'color' },
        { label: this.$t('Preview'), field: 'preview' },
        { label: this.$t('Users'), field: 'users', width: '140px' },
        { label: this.$t('Actions'), field: 'actions' },
      ],
      searchTerm: '',
      isProcessing: false,
      permissions: ['productivity.update'],
      checkingPermission: false,
      needPermissionCheck: false,
      pendingDelete: null,
      selectedTag: null,
    }
  },
  computed: {
    ...mapGetters('permissions', ['canManagePermissions']),
    filteredRows() {
      if (!this.searchTerm) return this.rows
      const term = this.searchTerm.toLowerCase()
      return this.rows.filter(
        r => (r.name || '').toLowerCase().includes(term)
          || (r.color || '').toLowerCase().includes(term)
          || (r.description || '').toLowerCase().includes(term),
      )
    },
  },
  mounted() {
    this.loadTags()
  },
  methods: {
    loadTags: _.debounce(function () {
      this.isProcessing = true
      useApollo.project
        .getProjectTags({
          projectId: this.$store.state.project.selectedProject,
          first: this.pageLength,
          page: this.currentPage,
        })
        .then(response => {
          const tags = response?.data?.project?.tags ?? null
          if (tags) {
            this.rows = (tags.data || []).map(t => ({
              id: t.id,
              name: t.name,
              color: t.color,
              description: t.description || '',
              users: t.users,
            }))
            this.totalItems = tags.total || (this.rows || []).length
          } else {
            this.rows = []
            this.totalItems = 0
          }
        })
        .catch(err => {
          console.error('Error loading project tags', err)
          this.rows = []
          this.totalItems = 0
        })
        .finally(() => {
          this.isProcessing = false
        })
    }, 1000),
    openAdd() {
      this.$refs.addEdit.open()
    },
    openEdit(row) {
      this.$refs.addEdit.open({ ...row })
    },
    confirmDelete(row) {
      this.pendingDelete = row
      this.$refs.deleteModal.show()
    },
    performDelete() {
      if (!this.pendingDelete) {
        this.$refs.deleteModal.hide()
        return
      }
      this.isProcessing = true
      const projectUid = this.$store.state.project.selectedProject
      useApollo.project
        .deleteTag({ projectUid, id: this.pendingDelete.id })
        .then(() => {
          this.pendingDelete = null
          this.$refs.deleteModal.hide()
          this.loadTags()
        })
        .catch(err => {
          console.error('Error deleting tag', err)
        })
        .finally(() => {
          this.isProcessing = false
        })
    },
  },
}
</script>

<style lang="scss">
.tags {
  .b-avatar-group-inner {
    align-items: center;
  }
}
</style>
